import { HttpClient, HttpContext } from "@angular/common/http";
import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { PropertyService } from "@core/services";
import { NotificationService } from "@core/services/notification/notification.service";
import { environment } from "@environments/environment";
import {
  AddAndAssignDeviceRequest,
  CUSTOM_ERROR_MESSAGE,
  Device,
  DeviceAction,
  ExtractSerialResponse,
  MdmDeviceCommand,
  MdmDeviceCommandHistory
} from "@shared/models";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
/**
 * Service for managing device data and executing device actions.
 * 
 * Provides comprehensive functionality for loading, managing, and controlling Mobile Device Management (MDM) devices.
 * Handles device operations including restart, reset, unassign, and device assignment to rooms. Includes serial number
 * extraction from images via AI and detailed activity tracking.
 * 
 * @example
 * ```typescript
 * constructor(private deviceService: DeviceService) {}
 * 
 * ngOnInit() {
 *   // Enable device loading and get all devices for current property
 *   this.deviceService.enableLoading();
 *   const devices = this.deviceService.devices();
 *   
 *   // Get device activities
 *   this.deviceService.getDeviceActivities('device-serial', propertyId).subscribe(activities => {
 *     console.log('Device activities:', activities);
 *   });
 *   
 *   // Perform device actions
 *   this.deviceService.performAction('device-id', 'restart', propertyId).subscribe(() => {
 *     console.log('Device restarted');
 *   });
 * }
 * ```
 * 
 * @remarks
 * - Uses Angular signals for reactive state management
 * - Implements lazy-loading of devices via enableLoading()
 * - Automatically reloads devices when selected property changes via effects
 * - Caches device list with force refresh capability
 * - All public properties are read-only signals or computed values
 * - Loading state is automatically managed across all operations
 * - Integrates with RoomService for device-room associations
 * 
 * @class DeviceService
 */
export class DeviceService {
  private readonly http = inject(HttpClient);
  private readonly propertyService = inject(PropertyService);
  private readonly notificationService = inject(NotificationService);

  private loadingSignal = signal<boolean>(false);
  private activitiesSignal = signal<MdmDeviceCommand[]>([]);
  private areDevicesNeededSignal = signal<boolean>(false);

  /**
   * Read-only signal indicating whether a device operation is currently in progress.
   * Used to display loading indicators in the UI during API calls.
   * 
   * @readonly
   * @type {Signal<boolean>}
   */
  readonly loading = this.loadingSignal.asReadonly();

  /**
   * Read-only signal containing the list of command activities for the currently loaded device.
   * Populated by getDeviceActivities() method.
   * 
   * @readonly
   * @type {Signal<MdmDeviceCommand[]>}
   */
  readonly activities = this.activitiesSignal.asReadonly();

  /**
   * Computed signal containing all devices for the currently selected property.
   * Automatically updates when the property selection changes.
   * Only loads devices after enableLoading() is called.
   * 
   * @returns {Device[]} Array of devices for the property
   * @returns {[]} Empty array if no devices or if loading failed
   * 
   * @readonly
   * @type {Signal<Device[]>}
   */
  readonly devices = computed(() => {
    if (this.devicesResource.error()) return [];
    return this.devicesResource.value() ?? [];
  });

  /**
   * Signal controlling whether a force reload of devices should occur.
   * Used internally by forceRefresh() to bypass cache.
   * 
   * @type {Signal<boolean>}
   */
  forceReload = signal<boolean>(false);

  constructor() {
    effect(() => {
      const property = this.propertyService.selectedProperty();
      if (property) {
        this.devicesResource.reload();
      }
    });
  }

  /**
   * Enables lazy-loading of devices for the currently selected property.
   * Must be called before devices are fetched. Automatically triggers device loading
   * when the property selection changes afterward.
   * 
   * @returns {void}
   * 
   * @remarks
   * - Devices are not fetched until this method is called
   * - Useful for lazy-loading to avoid fetching data not needed by all users
   * - Can be called multiple times without side effects
   * 
   * @example
   * ```typescript
   * ngOnInit() {
   *   this.deviceService.enableLoading();
   * }
   * ```
   */
  enableLoading = (): void => this.areDevicesNeededSignal.set(true);

  /**
   * Disables lazy-loading of devices for the currently selected property.
   * Prevents automatic device loading when the property selection changes.
   * 
   * @returns {void}
   * 
   * @remarks
   * - Useful for lazy-loading to avoid fetching data not needed by all users
   * - Can be called multiple times without side effects
   * 
   * @example
   * ```typescript
   * ngOnInit() {
   *   this.deviceService.disableLoading();
   * }
   * ```
   */
  disableLoading = (): void => this.areDevicesNeededSignal.set(false);

  /**
   * RxJS resource for managing device data caching and loading.
   * Only becomes active after enableLoading() is called.
   * Automatically triggers reload when the selected property changes.
   * 
   * @remarks
   * - Returns an empty array if no property is selected or enableLoading() hasn't been called
   * - Caches devices to avoid unnecessary API calls unless force reload is triggered
   * - Stream is only initiated when areDevicesNeededSignal is true
   */
  devicesResource = rxResource({
    params: () => {
      if (!this.areDevicesNeededSignal()) {
        return null;
      }
      const property = this.propertyService.selectedProperty();
      return { id: property?.placeId ?? null, force: this.forceReload() };
    },
    stream: (params) => {
      if (!params || !params.params?.id) {
        return of([] as Device[]);
      }

      return this.loadDevicesForPlace(params.params.id, params.params.force)
    },
    defaultValue: [] as Device[]
  });

  /**
   * Performs an action on a device (restart, reset, or unassign).
   * Automatically manages loading state and delegates to specific action methods.
   * 
   * @param {string} deviceId - The device ID (refId) to perform the action on
   * @param {DeviceAction} action - The action to perform: 'restart', 'reset', or 'unassign'
   * @param {number} propertyId - The ID of the property containing the device
   * @returns {Observable<void>} Observable that completes when the action succeeds
   * 
   * @remarks
   * - Sets loading state to true before executing the action
   * - Delegates to restartDevice(), resetDevice(), or unassignDevice() based on action type
   * - Returns error observable if action type is invalid
   * - Each action handles its own error handling and loading state reset
   * 
   * @throws {Error} If the action is not one of: 'restart', 'reset', 'unassign'
   * 
   * @example
   * ```typescript
   * this.deviceService.performAction('device-ref-123', 'restart', 1).subscribe({
   *   next: () => console.log('Device restarted'),
   *   error: (err) => console.error('Action failed:', err)
   * });
   * ```
   */
  performAction(deviceId: string, action: DeviceAction, propertyId: number): Observable<void> {
    this.loadingSignal.set(true);

    switch (action) {
      case "restart":
        return this.restartDevice(deviceId);
      case "reset":
        return this.resetDevice(deviceId, propertyId);
      case "unassign":
        return this.unassignDevice(deviceId);
      case "checkin":
        return this.performDailyCheckin(deviceId);
      case "blank-push":
        return this.refreshDevice(deviceId);
      default:
        this.loadingSignal.set(false);
        return throwError(() => new Error("Invalid device action"));
    }
  }

  /**
   * Adds a new device and assigns it to a room in the specified property.
   * Updates the devices cache with the new device or updates an existing device's room assignment.
   * 
   * @param {AddAndAssignDeviceRequest} request - The device details including serial number and target room ID
   * @param {number} propertyId - The ID of the property to add the device to
   * @returns {Observable<Device[]>} Observable emitting the updated list of devices
   * 
   * @remarks
   * - Sets loading state to true before the API call
   * - If device already exists in cache, updates its room assignment
   * - If device is new, adds it to the cached devices list
   * - Automatically resets loading state on success or error
   * - Uses the provided room ID from the request for assignment
   * 
   * @example
   * ```typescript
   * const request: AddAndAssignDeviceRequest = {
   *   deviceSerialNumber: 'SN-12345',
   *   roomRefId: 'room-456'
   * };
   * this.deviceService.addAndAssignDevice(request, 1).subscribe({
   *   next: (devices) => console.log('Device added:', devices),
   *   error: (err) => console.error('Failed to add device:', err)
   * });
   * ```
   */
  addAndAssignDevice(request: AddAndAssignDeviceRequest, propertyId: number): Observable<Device[]> {
    this.loadingSignal.set(true);

    return this.http
      .post<Device[]>(`${environment.mdmDeviceApiUrl}/places/${propertyId}/MdmDevices/AddAndAssign`, request)
      .pipe(
        tap((response: Device[]) => {
          this.loadingSignal.set(false);
          const currentDevices = this.devicesResource.value() ?? [];
          const deviceInList = currentDevices.find(d => d.serialNumber === request.deviceSerialNumber);
          let updatedDevices: Device[];
          if (deviceInList) {
            deviceInList.fkRoom = request.roomRefId!;
            updatedDevices = [...currentDevices];
          } else {
            updatedDevices = [...currentDevices, new Device(response[0])];
          }
          this.devicesResource.set(updatedDevices);
        }),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Extracts a device serial number from an image using AI-powered image processing.
   * Useful for quickly scanning device serial numbers from photos.
   * 
   * @param {File} imageFile - The image file containing the device serial number
   * @returns {Observable<ExtractSerialResponse>} Observable emitting the extracted serial number response
   * 
   * @remarks
   * - Sends the image as multipart form data to the API
   * - Sets loading state to true during processing
   * - Resets loading state on completion (success or error)
   * - Response contains the extracted serial number and extraction confidence
   * 
   * @example
   * ```typescript
   * const imageFile = new File([...], 'device-photo.jpg');
   * this.deviceService.extractSerialFromImage(imageFile).subscribe({
   *   next: (response) => console.log('Serial extracted:', response.serialNumber),
   *   error: (err) => console.error('Extraction failed:', err)
   * });
   * ```
   */
  extractSerialFromImage(imageFile: File): Observable<ExtractSerialResponse> {
    this.loadingSignal.set(true);

    const formData = new FormData();
    formData.append("image", imageFile);

    return this.http
      .post<ExtractSerialResponse>(
        `${environment.apiUrl}/extract-serial`,
        formData,
      )
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Fetches the command activity history for a specific device.
   * Retrieves all commands that have been executed on the device.
   * 
   * @param {string} deviceSerialNumber - The serial number of the device
   * @param {number} propertyId - The ID of the property containing the device
   * @returns {Observable<MdmDeviceCommandHistory>} Observable emitting the device's command history
   * 
   * @remarks
   * - Sets loading state to true before fetching activities
   * - Extracts the list of commands from the history response and updates the activities signal
   * - Automatically resets loading state on completion or error
   * - Activities are cached in the activities signal for display in the UI
   * - Can be used to show device command timeline or audit log
   * 
   * @example
   * ```typescript
   * this.deviceService.getDeviceActivities('SN-12345', 1).subscribe({
   *   next: (history) => {
   *     console.log('Device commands:', this.deviceService.activities());
   *   },
   *   error: (err) => console.error('Failed to load activities:', err)
   * });
   * ```
   */
  getDeviceActivities(deviceSerialNumber: string, propertyId: number): Observable<MdmDeviceCommandHistory> {
    this.loadingSignal.set(true);

    return this.http
      .get<
        MdmDeviceCommandHistory
      >(`${environment.mdmDeviceApiUrl}/places/${propertyId}/devices/${deviceSerialNumber}/commands`)
      .pipe(
        map((activities) => new MdmDeviceCommandHistory(activities)),
        tap((activities) => {
          this.activitiesSignal.set(activities.commands);
          this.loadingSignal.set(false);
        }),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Loads devices for a specific property from the API.
   * Implements caching logic to avoid redundant API calls.
   * 
   * @param {number} placeId - The ID of the property/place to load devices for
   * @param {boolean} [force=false] - If true, bypasses cache and forces a fresh API call
   * @returns {Observable<Device[]>} Observable emitting the array of devices for the property
   * 
   * @remarks
   * - Caches results and returns cached data if the same property is requested with force=false
   * - Maps raw API responses to Device model instances
   * - Sets and resets loading state during the API call
   * - Should not be called directly in most cases; use the devices signal instead
   * 
   * @example
   * ```typescript
   * this.deviceService.loadDevicesForPlace(1).subscribe(devices => {
   *   console.log('Loaded devices:', devices);
   * });
   * 
   * // Force refresh from API
   * this.deviceService.loadDevicesForPlace(1, true).subscribe(devices => {
   *   console.log('Fresh devices:', devices);
   * });
   * ```
   */
  loadDevicesForPlace(placeId: number, force = false): Observable<Device[]> {
    const currentDevices = this.devicesResource.value() ?? [];
    if (!force && currentDevices.length > 0 && currentDevices[0].fkPlace === placeId) {
      return of(currentDevices);
    }
    this.loadingSignal.set(true);

    let httpContext = new HttpContext().set(CUSTOM_ERROR_MESSAGE, "Failed to load devices. Please try again.");

    return this.http
      .get<Device[]>(`${environment.mdmDeviceApiUrl}/places/${placeId}/devices`, { context: httpContext })
      .pipe(
        map((devices) => devices.map((device) => new Device(device)))
      );
  }

  /**
   * Sends a restart command to a device.
   * 
   * @param {string} deviceRefId - The reference ID of the device to restart
   * @returns {Observable<void>} Observable that completes when the command is sent
   * 
   * @remarks
   * - Sets loading state to true before sending the command
   * - Resets loading state on completion or error
   * - The actual device restart may take several seconds
   * - Returns immediately after the API acknowledges the command
   * 
   * @example
   * ```typescript
   * this.deviceService.restartDevice('device-ref-123').subscribe({
   *   next: () => console.log('Restart command sent'),
   *   error: (err) => console.error('Failed to restart:', err)
   * });
   * ```
   */
  restartDevice(deviceRefId: string): Observable<void> {
    this.loadingSignal.set(true);

    return this.http
      .post<void>(`${environment.mdmDeviceApiUrl}/command/${deviceRefId}/restartDevice`, {})
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Sends a factory reset (wipe) command to a device.
   * This erases all data from the device and resets it to factory defaults.
   * 
   * @param {string} deviceRefId - The reference ID of the device to reset
   * @param {number} propertyId - The ID of the property containing the device
   * @returns {Observable<void>} Observable that completes when the command is sent
   * 
   * @remarks
   * - Sets loading state to true before sending the command
   * - Resets loading state on completion or error
   * - This is a destructive operation - all device data will be erased
   * - The actual wipe process may take several minutes
   * - Returns immediately after the API acknowledges the command
   * - Typically used to remove sensitive data before device disposal or reassignment
   * 
   * @example
   * ```typescript
   * this.deviceService.resetDevice('device-ref-123', 1).subscribe({
   *   next: () => console.log('Wipe command sent'),
   *   error: (err) => console.error('Failed to reset:', err)
   * });
   * ```
   */
  resetDevice(deviceRefId: string, propertyId: number): Observable<void> {
    this.loadingSignal.set(true);

    return this.http
      .post<void>(`${environment.mdmDeviceApiUrl}/command/${propertyId}/WipeDevice/${deviceRefId}`, {})
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Unassigns a device from its currently assigned room.
   * Clears the room assignment while keeping the device in the system.
   * 
   * @param {string} deviceRefId - The reference ID of the device to unassign
   * @returns {Observable<void>} Observable that completes when the unassignment succeeds
   * 
   * @remarks
   * - Sets loading state to true before sending the command
   * - Automatically updates the devices cache by removing the room assignment
   * - Sets fkRoom property to null in the cached device
   * - Resets loading state on completion or error
   * - The device remains in the system, just no longer assigned to a room
   * 
   * @example
   * ```typescript
   * this.deviceService.unassignDevice('device-ref-123').subscribe({
   *   next: () => console.log('Device unassigned'),
   *   error: (err) => console.error('Failed to unassign:', err)
   * });
   * ```
   */
  unassignDevice(deviceRefId: string): Observable<void> {
    this.loadingSignal.set(true);

    return this.http
      .post<void>(`${environment.mdmDeviceApiUrl}/command/ClearRoomAssignment/${deviceRefId}`, {})
      .pipe(
        tap(() => {
          const currentDevices = this.devicesResource.value() ?? [];
          const updatedDevices = currentDevices.map(d =>
            d.refId === deviceRefId
              ? { ...d, fkRoom: null }
              : d
          );
          this.devicesResource.set(updatedDevices);
          this.loadingSignal.set(false);
        }),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  refreshDevice(deviceRefId: string): Observable<void> {
    return this.http.post<void>(`${environment.mdmDeviceApiUrl}/command/${deviceRefId}/RefreshDevice`, {})
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  performDailyCheckin(deviceRefId: string): Observable<void> {
    return this.http.post<void>(`${environment.mdmDeviceApiUrl}/command/${deviceRefId}/DailyCheckin`, {})
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Forces a refresh of the devices cache from the API.
   * Bypasses the normal caching mechanism and fetches fresh data.
   * 
   * @returns {void}
   * 
   * @remarks
   * - Uses queueMicrotask to ensure the signal toggle happens in the microtask queue
   * - Triggers a reload of the devicesResource with force=true
   * - Useful for manual refresh functionality in the UI
   * - Should be called sparingly to avoid excessive API calls
   * 
   * @example
   * ```typescript
   * // Provide a refresh button in the UI
   * onRefreshClick() {
   *   this.deviceService.forceRefresh();
   * }
   * ```
   */
  forceRefresh(): void {
    this.forceReload.set(true);
    queueMicrotask(() => this.forceReload.set(false));
  }


  /**
   * Clears all cached devices from the service.
   * Useful for cleanup when switching properties or during logout.
   * 
   * @returns {void}
   * 
   * @remarks
   * - Resets the devices cache to an empty array
   * - Does not affect the RoomService cache
   * - The devices signal will re-compute to an empty array
   * 
   * @example
   * ```typescript
   * // Clear devices when logging out
   * logout() {
   *   this.deviceService.clearDevices();
   *   // ... other cleanup
   * }
   * ```
   */
  clearDevices(): void {
    this.devicesResource.set([]);
  }
}
